import { useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const TOC = [
  { id: 'get-started', label: 'Get started' },
  { id: 'pages', label: 'What each page does' },
  { id: 'booking', label: 'Booking & calendars' },
  { id: 'crm', label: 'CRM, Slack & email' },
  { id: 'field-map', label: 'Webhook field map' },
  { id: 'payloads', label: 'What we send' },
  { id: 'plans', label: 'Plans' },
] as const;

type FieldRow = { path: string; meaning: string; when: string };

const WEBHOOK_COMMON_FIELDS: FieldRow[] = [
  { path: 'source', meaning: 'Always "supa-chat"', when: 'Every event' },
  { path: 'event', meaning: 'booking_completed or booking_prebooking', when: 'Every event' },
  { path: 'stage', meaning: 'Lifecycle step (lead, slot, handoff, …)', when: 'Prebooking only' },
  { path: 'action_id', meaning: 'Unique id for this action', when: 'Every event' },
  { path: 'site_id', meaning: 'Your site id in our system', when: 'Every event' },
  { path: 'session_id', meaning: 'Chat session id', when: 'Every event' },
];

const WEBHOOK_LEAD_FIELDS: FieldRow[] = [
  { path: 'lead.name', meaning: 'Visitor full name', when: 'When collected' },
  { path: 'lead.email', meaning: 'Visitor email', when: 'When collected' },
  { path: 'lead.phone', meaning: 'Phone number', when: 'When collected' },
  { path: 'lead.company', meaning: 'Company name', when: 'When collected' },
  { path: 'lead.address', meaning: 'Address', when: 'When collected' },
  { path: 'lead.job_title', meaning: 'Job title', when: 'When collected' },
  { path: 'lead.notes', meaning: 'Free-text notes', when: 'When collected' },
  { path: 'lead.marketing_consent', meaning: 'Marketing opt-in (true/false)', when: 'When collected' },
  { path: 'lead.budget', meaning: 'Budget mentioned in chat', when: 'If mentioned' },
  { path: 'lead.timeline', meaning: 'Timeline mentioned in chat', when: 'If mentioned' },
];

const WEBHOOK_COMPLETED_FIELDS: FieldRow[] = [
  { path: 'attribution.page_url', meaning: 'Page where the widget ran', when: 'Completed booking' },
  { path: 'attribution.utm_source', meaning: 'UTM source', when: 'If present' },
  { path: 'attribution.utm_medium', meaning: 'UTM medium', when: 'If present' },
  { path: 'attribution.utm_campaign', meaning: 'UTM campaign', when: 'If present' },
  { path: 'attribution.utm_term', meaning: 'UTM term', when: 'If present' },
  { path: 'attribution.utm_content', meaning: 'UTM content', when: 'If present' },
  { path: 'attribution.referrer', meaning: 'HTTP referrer', when: 'If present' },
  { path: 'booking.label', meaning: 'Human-readable slot label', when: 'Completed booking' },
  { path: 'booking.start', meaning: 'Slot start (ISO datetime)', when: 'Completed booking' },
  { path: 'booking.end', meaning: 'Slot end (ISO datetime)', when: 'Completed booking' },
  { path: 'booking.timezone', meaning: 'Timezone for the booking', when: 'Completed booking' },
  { path: 'booking.service', meaning: 'Service / procedure name', when: 'Completed booking' },
  { path: 'booking.specialist', meaning: 'Specialist / staff name', when: 'Completed booking' },
  { path: 'booking.duration_minutes', meaning: 'Duration in minutes', when: 'Completed booking' },
  { path: 'booking.price', meaning: 'Price amount', when: 'Completed booking' },
  { path: 'booking.price_currency', meaning: 'Currency code (e.g. EUR)', when: 'Completed booking' },
  { path: 'booking.price_label', meaning: 'Display price (e.g. €50)', when: 'Completed booking' },
  { path: 'booking.status', meaning: 'Usually booking_completed', when: 'Completed booking' },
];

const WEBHOOK_PREBOOKING_FIELDS: FieldRow[] = [
  { path: 'booking_url', meaning: 'Fallback / Calendly scheduling link', when: 'Prebooking (when set)' },
  { path: 'booking_preferences.booked_for', meaning: 'Chosen service', when: 'Prebooking (when set)' },
  { path: 'booking_preferences.booked_to', meaning: 'Chosen specialist', when: 'Prebooking (when set)' },
  { path: 'booking_preferences.duration_minutes', meaning: 'Duration in minutes', when: 'Prebooking (when set)' },
  { path: 'booking_preferences.price_amount', meaning: 'Price amount', when: 'Prebooking (when set)' },
  { path: 'booking_preferences.price_currency', meaning: 'Currency code', when: 'Prebooking (when set)' },
  { path: 'booking_preferences.price_label', meaning: 'Display price', when: 'Prebooking (when set)' },
  { path: 'price', meaning: 'Price amount (top-level copy)', when: 'Prebooking (when set)' },
  { path: 'price_currency', meaning: 'Currency (top-level copy)', when: 'Prebooking (when set)' },
  { path: 'price_label', meaning: 'Display price (top-level copy)', when: 'Prebooking (when set)' },
];

export function DashboardHowItWorksLink({ hash }: { hash: string }) {
  return (
    <Link to={`/dashboard/help#${hash}`} className="underline underline-offset-2">
      How this works
    </Link>
  );
}

function Go({ to, children }: { to: string; children: string }) {
  return (
    <Button asChild size="sm" variant="outline">
      <Link to={to}>{children}</Link>
    </Button>
  );
}

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <Card id={id} className="scroll-mt-6">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm text-muted-foreground">{children}</CardContent>
    </Card>
  );
}

function FieldMapTable({ rows }: { rows: FieldRow[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="min-w-[11rem]">Our field (map from)</TableHead>
            <TableHead>What it is</TableHead>
            <TableHead className="min-w-[8rem]">When</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.path}>
              <TableCell className="align-top font-mono text-xs text-foreground">{row.path}</TableCell>
              <TableCell className="align-top text-foreground/90">{row.meaning}</TableCell>
              <TableCell className="align-top text-xs">{row.when}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function DashboardHelpPage() {
  const location = useLocation();

  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [location.hash]);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Help</h2>
        <p className="text-sm text-muted-foreground">
          How to set up the assistant, where each setting lives, and what gets sent to calendars and CRM.
        </p>
      </div>

      <nav aria-label="Help sections" className="flex flex-wrap gap-2">
        {TOC.map((item) => (
          <Link
            key={item.id}
            to={{ hash: `#${item.id}` }}
            className="rounded-md border border-border px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {item.label}
          </Link>
        ))}
      </nav>

      <Section id="get-started" title="Get started">
        <p className="text-foreground">Do these in order. Overview also lists whatever is still missing.</p>
        <ol className="list-decimal space-y-2 pl-5">
          <li>
            <strong className="text-foreground">Add funds</strong> on Billing so chat is not gated. If the admin already added funds, you can skip this step.
          </li>
          <li>
            <strong className="text-foreground">Claim and crawl</strong> your site on Content. That is what the bot
            answers from (plus any FAQs you add).
          </li>
          <li>
            <strong className="text-foreground">Paste the embed snippet</strong> from Widget onto your website.
          </li>
          <li>
            <strong className="text-foreground">Catalog</strong> (Pro): services, specialists, and prices the bot
            can offer.
          </li>
          <li>
            <strong className="text-foreground">Schedule</strong>: connect Google Calendar or Calendly{' '}
            <em>first</em>, pick the target calendar / event type, set weekly hours, and optionally a fallback
            scheduling link. In-chat booking cannot be enabled until the calendar is connected and the target is
            chosen.
          </li>
          <li>
            <strong className="text-foreground">Automation</strong>: turn on booking / leads / CRM, extra lead fields,
            Slack / team email. <strong className="text-foreground">Connect Gmail</strong> under Notifications &amp; CRM
            so visitor confirmation / reminder / outcome emails send from your Google account (this is separate from
            calendar — disconnecting Gmail does not turn off booking). HubSpot token or webhook URL. After the calendar
            is ready, turn on <strong className="text-foreground">Allow booking inside chat</strong> if you want time
            slots in the widget (see Booking &amp; calendars). You can enable Booking earlier with only a fallback
            link. Save.
          </li>
          <li>
            <strong className="text-foreground">Bookings</strong>: watch incoming appointments and retry calendar sync
            if something failed.
          </li>
        </ol>
        <div className="flex flex-wrap gap-2 pt-1">
          <Go to="/dashboard">Overview</Go>
          <Go to="/dashboard/billing">Billing</Go>
          <Go to="/dashboard/content">Content</Go>
          <Go to="/dashboard/widget">Widget</Go>
        </div>
      </Section>

      <Section id="pages" title="What each page does">
        <ul className="space-y-3">
          <li>
            <strong className="text-foreground">Overview</strong> — balance, calendar status, next steps, usage, recent
            bookings.
          </li>
          <li>
            <strong className="text-foreground">Content</strong> — your website, crawl, and extra knowledge (FAQs). No
            crawl means the widget has little to answer from.
          </li>
          <li>
            <strong className="text-foreground">Widget</strong> — copy the embed snippet. Customize look if your plan
            allows it.
          </li>
          <li>
            <strong className="text-foreground">Billing</strong> — prepaid balance, payment method, auto-recharge,
            start/stop the chatbot.
          </li>
          <li>
            <strong className="text-foreground">Bookings</strong> — appointments from chat, status, calendar sync.
          </li>
          <li>
            <strong className="text-foreground">Schedule</strong> — Google or Calendly connection, hours, breaks, time
            off, fallback scheduling link.
          </li>
          <li>
            <strong className="text-foreground">Catalog</strong> — specialists, services, prices shown in chat and sent
            with booking events.
          </li>
          <li>
            <strong className="text-foreground">Automation</strong> — plan capabilities (booking, leads, CRM), the{' '}
            <strong className="text-foreground">Allow booking inside chat</strong> toggle, extra lead fields,{' '}
            <strong className="text-foreground">Connect Gmail</strong> for visitor booking emails, team notifications,
            HubSpot or CRM webhook.
          </li>
        </ul>
        <p>
          Owners and editors can save settings. Viewers are read-only. Change your password from the account menu
          (Security).
        </p>
      </Section>

      <Section id="booking" title="Booking & calendars">
        <CardDescription className="text-foreground">Connect calendar before in-chat booking</CardDescription>
        <p>
          Recommended order: <strong className="text-foreground">Schedule → connect Google or Calendly → choose
          target → save</strong>, then on Automation turn on Booking and{' '}
          <strong className="text-foreground">Allow booking inside chat</strong>. You may enable Booking earlier with
          only a fallback scheduler link (no calendar yet) — visitors get an external &quot;Open Booking Scheduler&quot;
          button instead of in-widget slots.
        </p>
        <CardDescription className="text-foreground">Two booking toggles (Automation)</CardDescription>
        <p>
          Booking uses <strong className="text-foreground">two separate switches</strong> on Automation. Turning on only
          the first one is a common setup mistake — visitors fill in the contact form but never see time slots.
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong className="text-foreground">Booking enabled</strong> — lets the assistant detect booking intent and
            show the lead form (name, email, and any extra fields you enabled). Can be on before calendar connect if a
            fallback link is set.
          </li>
          <li>
            <strong className="text-foreground">Allow booking inside chat</strong> — after the form, shows a{' '}
            <strong className="text-foreground">time-slot picker</strong> in the widget and creates the appointment on
            your connected calendar. Requires Booking enabled{' '}
            <strong className="text-foreground">and</strong> a connected calendar with a target chosen. Locked off on
            Starter and while calendar is disconnected or still needs setup.
          </li>
        </ul>
        <p>What visitors see after they submit the form:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong className="text-foreground">Both toggles on + calendar ready</strong> — slot picker with available
            times (set weekly hours on Schedule). Appointments are written to your connected calendar.
          </li>
          <li>
            <strong className="text-foreground">Booking on, in-chat off + fallback link set</strong> — an{' '}
            <strong className="text-foreground">Open Booking Scheduler</strong> button (Calendly or your scheduling URL
            from Schedule → fallback scheduling link). No calendar OAuth required for this path.
          </li>
          <li>
            <strong className="text-foreground">Booking on, in-chat off, no fallback link</strong> —{' '}
            <strong className="text-foreground">not allowed</strong>. Saving is blocked because visitors would fill the
            form and hit a dead end. Turn on <em>Allow booking inside chat</em> (after connecting a calendar) or add a
            fallback scheduling link first.
          </li>
          <li>
            <strong className="text-foreground">In-chat on without a ready calendar</strong> —{' '}
            <strong className="text-foreground">not allowed</strong>. Saving is blocked, and the chat runtime will not
            offer or confirm in-widget slots (avoids bookings that never appear on a calendar).
          </li>
        </ul>
        <p>
          <strong className="text-foreground">Disconnecting calendar</strong> turns off &quot;Allow booking inside
          chat&quot;. Booking stays enabled only if a fallback scheduler link is set; otherwise Booking is turned off
          too. <strong className="text-foreground">Disconnecting Gmail</strong> (Automation) only changes how visitor
          confirmation emails are sent — it does <em>not</em> turn off Booking or calendar sync.
        </p>
        <p>
          <strong className="text-foreground">Google Calendar:</strong> Connect, then choose which calendar new events
          go to, then enable in-chat booking on Automation.
        </p>
        <p>
          <strong className="text-foreground">Calendly:</strong> Connect, then choose the event type. Hours in this
          dashboard affect in-chat slot suggestions; Calendly still uses its own availability for Calendly-hosted
          links.
        </p>
        <p>
          Catalog (service + specialist + price) is what the bot offers and what is attached to the booking event. Hours,
          breaks, and time off control when in-chat slots are suggested.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Go to="/dashboard/schedule">Open Schedule</Go>
          <Go to="/dashboard/automation">Open Automation</Go>
        </div>
      </Section>

      <Section id="crm" title="CRM, Slack & email">
        <p>
          Chat does not write to HubSpot or Slack directly. After a booking, lead, or handoff, the app sends one event
          to automation. That workflow then emails, posts to Slack, upserts HubSpot, or POSTs your webhook — only if
          the matching toggles and credentials are saved.
        </p>
        <p>
          On Automation: turn on <strong className="text-foreground">Sync leads to CRM</strong>, pick HubSpot or
          webhook, paste the HubSpot access token or HTTPS webhook URL, then Save. Booking alerts use the Slack webhook
          and team email on that same page.
        </p>
        <p>
          <strong className="text-foreground">Connect Gmail</strong> (Notifications &amp; CRM on Automation) authorizes
          Google so visitor confirmation, reminder, and outcome emails are sent <em>from your Gmail address</em> (true
          From). You will be asked to sign in with Google and allow send permission — this is separate from calendar
          connect. Disconnecting Gmail does <strong className="text-foreground">not</strong> disable Booking or
          in-chat slots; only email delivery falls back to EmbedReply with Reply-To from{' '}
          <strong className="text-foreground">Customer booking emails from</strong> (defaults to the site owner). Locked
          on Starter. <strong className="text-foreground">Team email</strong> is separate — internal follow-ups only,
          not emails to visitors.
        </p>

        <CardDescription className="pt-1 text-foreground">CRM webhook (Other CRM)</CardDescription>
        <p>
          Use this when you are not on HubSpot — for example Zapier, Make, Pipedrive, Salesforce, or your own API.
          We <strong className="text-foreground">POST JSON</strong> to the URL you paste. If you set an auth token, it is
          sent as <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground">Authorization: Bearer …</code>.
        </p>
        <p>
          Your CRM (or Zapier/Make) must <strong className="text-foreground">map our field names</strong> to its own
          (e.g. <code className="font-mono text-xs">lead.email</code> → Email). We do not speak Salesforce or Pipedrive
          APIs natively — the webhook is a fixed JSON shape you connect once. Use the{' '}
          <Link to={{ hash: '#field-map' }} className="underline underline-offset-2">
            field map table
          </Link>{' '}
          below when setting up the mapping.
        </p>
        <p>When a POST is sent:</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong className="text-foreground">Completed booking</strong> — full lead + slot + service/specialist/price
            + page/UTM attribution (<code className="font-mono text-xs">event: booking_completed</code>).
          </li>
          <li>
            <strong className="text-foreground">Earlier moments</strong> — lead captured, slot picked, Calendly fallback
            link shown, or human handoff submitted (
            <code className="font-mono text-xs">event: booking_prebooking</code> plus a <code className="font-mono text-xs">stage</code>).
          </li>
        </ul>

        <p>
          <strong className="text-foreground">Human handoff</strong> is not live chat. The visitor opts in with a form
          (name / contact / issue). Slack is notified only when they submit. Use the dedicated handoff Slack webhook,
          not the booking one.
        </p>
        <p>
          HubSpot token: private app or static token with contacts read/write and meetings write. Add deals write if
          you enable deal creation (Pro). Optional custom contact properties should be named with a{' '}
          <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs text-foreground">supachat_</code> prefix.
        </p>
        <Go to="/dashboard/automation">Open Automation</Go>
      </Section>

      <Section id="field-map" title="Webhook field map">
        <p className="text-foreground">
          Map the left column to whatever your CRM calls the same thing. Empty or unused fields may be missing or null.
        </p>
        <CardDescription className="text-foreground">Always present</CardDescription>
        <FieldMapTable rows={WEBHOOK_COMMON_FIELDS} />
        <CardDescription className="pt-2 text-foreground">Lead (visitor contact)</CardDescription>
        <FieldMapTable rows={WEBHOOK_LEAD_FIELDS} />
        <CardDescription className="pt-2 text-foreground">Completed booking only</CardDescription>
        <FieldMapTable rows={WEBHOOK_COMPLETED_FIELDS} />
        <CardDescription className="pt-2 text-foreground">Prebooking events only</CardDescription>
        <FieldMapTable rows={WEBHOOK_PREBOOKING_FIELDS} />
        <p className="text-xs">
          Prebooking <code className="font-mono">stage</code> values:{' '}
          <code className="font-mono">lead_capture_completed</code>,{' '}
          <code className="font-mono">booking_slot_selection</code>,{' '}
          <code className="font-mono">booking_requested</code>,{' '}
          <code className="font-mono">booking_fallback_link</code>,{' '}
          <code className="font-mono">booking_received_no_scheduler</code>,{' '}
          <code className="font-mono">human_handoff_completed</code>.
        </p>
      </Section>

      <Section id="payloads" title="What we send">
        <CardDescription className="text-foreground">HubSpot (when provider is HubSpot and sync is on)</CardDescription>
        <p>
          Needs an email. Standard contact fields: email, first name, last name, phone, company, address, job title,
          lifecycle stage (<code className="font-mono text-xs">lead</code>, or{' '}
          <code className="font-mono text-xs">opportunity</code> after a completed booking).
        </p>
        <p>
          Optional custom properties (skipped if they do not exist in your portal): last booking label, service,
          specialist, start, price; source page URL; UTM source/medium/campaign/term/content; notes; marketing
          consent. Standalone lead capture sends standard fields only.
        </p>

        <CardDescription className="pt-2 text-foreground">CRM webhook — sample JSON</CardDescription>
        <p>
          HTTPS <code className="font-mono text-xs">POST</code> with{' '}
          <code className="font-mono text-xs">Content-Type: application/json</code>. Optional{' '}
          <code className="font-mono text-xs">Authorization: Bearer &lt;token&gt;</code>. For a flat list of paths to
          map, see{' '}
          <Link to={{ hash: '#field-map' }} className="underline underline-offset-2">
            Webhook field map
          </Link>
          .
        </p>
        <details className="rounded-md border border-border bg-muted/40 p-3">
          <summary className="cursor-pointer text-foreground">Completed booking body</summary>
          <pre className="mt-3 overflow-x-auto text-xs leading-relaxed text-foreground">{`{
  "event": "booking_completed",
  "action_id": "…",
  "site_id": 100,
  "session_id": "…",
  "lead": {
    "name": "Jane Smith",
    "email": "jane@example.com",
    "phone": "+359…",
    "company": "Acme",
    "address": "…",
    "job_title": "…",
    "notes": "…",
    "marketing_consent": true
  },
  "attribution": {
    "page_url": "https://yoursite.com/…",
    "utm_source": "…",
    "utm_medium": "…",
    "utm_campaign": "…",
    "referrer": "…"
  },
  "booking": {
    "label": "Tue 10:00",
    "start": "2026-09-01T10:00:00+03:00",
    "end": "2026-09-01T10:30:00+03:00",
    "timezone": "Europe/Sofia",
    "service": "Consultation",
    "specialist": "Anna",
    "duration_minutes": 30,
    "price": "50",
    "price_currency": "EUR",
    "price_label": "€50",
    "status": "booking_completed"
  },
  "source": "supa-chat"
}`}</pre>
        </details>
        <details className="rounded-md border border-border bg-muted/40 p-3">
          <summary className="cursor-pointer text-foreground">Earlier stages (lead, slot, handoff, fallback link)</summary>
          <pre className="mt-3 overflow-x-auto text-xs leading-relaxed text-foreground">{`{
  "event": "booking_prebooking",
  "stage": "lead_capture_completed",
  "action_id": "…",
  "site_id": 100,
  "session_id": "…",
  "lead": { "name", "email", "phone", … },
  "booking_url": "https://calendly.com/…",
  "booking_preferences": {
    "booked_for": "Consultation",
    "booked_to": "Anna",
    "duration_minutes": 30,
    "price_amount": "50",
    "price_currency": "EUR",
    "price_label": "€50"
  },
  "price": "50",
  "price_currency": "EUR",
  "price_label": "€50",
  "source": "supa-chat"
}`}</pre>
        </details>
        <p>
          Tip: point the URL at a Zapier/Make “Catch Hook”, send a test booking, then map using the field table above.
        </p>

        <CardDescription className="pt-2 text-foreground">Calendly / Google</CardDescription>
        <p>
          No CRM-style JSON is posted to Calendly. Connecting OAuth and choosing an event type (or Google calendar)
          is how events are created. Slack incoming webhooks receive a short text alert, not the full CRM payload.
        </p>
      </Section>

      <Section id="plans" title="Plans">
        <p>
          <strong className="text-foreground">Starter</strong> is chat-first. Booking, lead capture, and CRM sync are
          locked. You can still enable human handoff (needs the handoff Slack webhook).
        </p>
        <p>
          <strong className="text-foreground">Pro</strong> unlocks booking, leads, and CRM. It also controls how
          proactive the assistant is. Only an admin can change the plan tier.
        </p>
        <Go to="/dashboard/automation">Plan &amp; capabilities</Go>
      </Section>
    </div>
  );
}
